import { useCallback, useEffect } from 'react';
import { useDefault, useToggle } from 'react-use';

import useAppSelector from '~hooks/useAppSelector';
import { InputAutoComplete } from '~modules/media-library/view/SearchBar/InputAutoComplete';
import { InputSearch } from '~modules/media-library/view/SearchBar/InputSearch';
import { selectAddonsIsMediaLibraryEnabled } from '~store/selectors';

type SearchBarProps = {
  onQuery?: (query: string) => void;
  defaultValue?: string;
  className?: string;
};

export function SearchBar({ onQuery, defaultValue = '', className = '' }: SearchBarProps) {
  const [hasFocus, toggleFocus] = useToggle(false);
  const [query, setQuery] = useDefault('', defaultValue);
  const isMediaLibraryEnabled = useAppSelector(selectAddonsIsMediaLibraryEnabled);

  const handleOnEnter = useCallback(
    (optionalQuery?: string) => {
      setQuery(optionalQuery || query);
      onQuery?.(optionalQuery || query);
    },
    [query, onQuery, setQuery],
  );

  const handleOnClear = useCallback(() => {
    setQuery('');
    onQuery('');
  }, [onQuery, setQuery]);

  // This effect listens to changes of the default value. This is important when switching
  // between collections.
  // The 'query' has to change but only if the input is out of focus
  useEffect(() => {
    if (hasFocus) return;
    //if (!query) return;
    //if (!defaultValue) return;
    if (defaultValue === query) return;
    setQuery(defaultValue);
  }, [defaultValue, hasFocus, query, setQuery]);

  if (isMediaLibraryEnabled) {
    return (
      <div className={`ml--searchbar ${className}`}>
        <InputAutoComplete
          value={query}
          onChange={setQuery}
          onEnter={handleOnEnter}
          onClear={handleOnClear}
          onFocus={toggleFocus}
          onBlur={toggleFocus}
        />
      </div>
    );
  }

  return (
    <div className={`ml--searchbar ${className}`}>
      <InputSearch
        value={query}
        onChange={setQuery}
        onEnter={handleOnEnter}
        onClear={handleOnClear}
        onFocus={toggleFocus}
        onBlur={toggleFocus}
      />
    </div>
  );
}
